// Kod sztuki sprzętu: co nadrukować na etykiecie i jak rozpoznać kod zeskanowany z naklejki.
//
// Jedna reguła dla WSZYSTKICH miejsc, które czytają kody (szukajka magazynu, nowa rezerwacja,
// wydanie/zwrot, backend `GET /api/scan/lookup`) i dla drukarki etykiet — inaczej etykieta
// wydrukowana w jednym miejscu nie dawałaby się rozpoznać w drugim.
//
// Kolejność na etykiecie: numer inwentarzowy → kod kreskowy/QR producenta → kod systemowy.
// Kod systemowy `RNT-<id>` istnieje dlatego, że numer inwentarzowy jest OPCJONALNY (E.7), a firma
// bez numeracji też musi móc okleić sprzęt. Opiera się na identyfikatorze wiersza, więc NIE
// zmienia się, gdy firma później nada numer — stara naklejka dalej działa.

export const SYSTEM_CODE_PREFIX = 'RNT-';
const SYSTEM_CODE_RE = /^RNT-(\d{1,10})$/i;

// Code 128 koduje wyłącznie drukowalne ASCII. Numer z polską literą („NRT-ŻÓŁ-1") wywaliłby
// generator, więc taka sztuka dostaje w kodzie KRESKOWYM kod systemowy (QR koduje UTF-8 bez problemu).
const CODE128_SAFE_RE = /^[\x20-\x7E]{1,80}$/;

const clean = (v) => String(v == null ? '' : v).trim();
const lower = (v) => clean(v).toLowerCase();

function metadataOf(item) {
  const m = item && item.metadata;
  if (!m) return null;
  if (typeof m === 'object') return m;
  try { const parsed = JSON.parse(m); return parsed && typeof parsed === 'object' ? parsed : null; } catch { return null; }
}

/** Kod kreskowy/QR producenta zapisany przy sztuce (pole `barcode`, zwykle w `metadata`). */
export function barcodeOf(item) {
  if (!item) return '';
  if (clean(item.barcode)) return clean(item.barcode);
  const meta = metadataOf(item);
  return meta ? clean(meta.barcode) : '';
}

export function systemCodeFor(id) {
  return `${SYSTEM_CODE_PREFIX}${id}`;
}

/** Identyfikator z kodu systemowego albo null. */
export function parseSystemCode(code) {
  const m = SYSTEM_CODE_RE.exec(clean(code));
  return m ? Number(m[1]) : null;
}

/** Wartość do zakodowania w QR i do wypisania pod kodem. */
export function labelCodeFor(item) {
  if (!item) return '';
  return clean(item.inventory_number) || barcodeOf(item) || systemCodeFor(item.id);
}

export function isCode128Safe(code) {
  return CODE128_SAFE_RE.test(clean(code));
}

/** Wartość do kodu KRESKOWEGO: jak `labelCodeFor`, chyba że ta nie mieści się w Code 128. */
export function barcodeValueFor(item) {
  const code = labelCodeFor(item);
  return isCode128Safe(code) ? code : systemCodeFor(item.id);
}

/**
 * Sztuka z listy odpowiadająca zeskanowanemu kodowi albo null.
 *
 * Dopasowanie jest DOKŁADNE (bez wielkości liter i skrajnych spacji), nie „zawiera": skan to
 * identyfikator, a nie fraza wyszukiwania — „A1" nie może trafić w „A10".
 */
export function findEquipmentByCode(code, items) {
  const wanted = lower(code);
  if (!wanted || !Array.isArray(items)) return null;

  const systemId = parseSystemCode(code);
  if (systemId != null) {
    const hit = items.find((it) => Number(it && it.id) === systemId);
    if (hit) return hit;
  }

  return items.find((it) => it && lower(it.inventory_number) === wanted)
    || items.find((it) => it && barcodeOf(it) && lower(barcodeOf(it)) === wanted)
    || null;
}
